import { firestore } from '@/utils/firebase';
import {
	addDoc,
	collection,
	doc,
	getDoc,
	getDocs,
	query,
	Timestamp,
	where,
} from 'firebase/firestore';
import { getActingUser } from '../user/userApi';
import { DEAL_STATUS_TYPES, isFirebaseId } from '@/utils/constants';
import { notificationsApi } from '../notifications/notificationsApi';
import { canViewGroup, queryByOwnerId, queryViewableRecords } from '@/utils/firebaseService';

export const DEALS_COLLECTION = 'deals';

// Accepts either a Firestore deal doc (clientDocId) or an Insight-enriched row
// (ClientDocId).
export const getDealClientId = (dealRow) =>
	(dealRow?.clientDocId ?? dealRow?.ClientDocId)?.toString();

/**
 * Resolves a deal from its Firestore doc id (its `dealId`) or, for a numeric id from an
 * old link, its `dealHubspotId` among the deals the user can see. Returns an existing
 * snapshot, or undefined.
 */
export const getDealById = async (dealId, user) => {
	if (dealId == null) return;
	if (isFirebaseId(dealId)) {
		const dealDoc = await getDoc(doc(collection(firestore, DEALS_COLLECTION), dealId));
		return dealDoc.exists() ? dealDoc : undefined;
	}
	const docs = await queryViewableRecords({
		collectionRef: collection(firestore, DEALS_COLLECTION),
		constraints: [where('dealHubspotId', '==', dealId)],
		user,
		ownerFields: ['ownerDocId', 'clientOwnerDocId'],
	});
	return docs[0];
};

export const createArchivedActivity = async ({ dispatch, getState, properties, dealId }) => {
	try {
		const userData = await getActingUser(dispatch, getState);

		const { userId } = userData ?? {};

		const userRef = doc(firestore, 'user', userId);

		const body = {
			activityType:
				properties?.status === DEAL_STATUS_TYPES.archived
					? 'dealArchived'
					: 'dealUnarchived',
			dealId,
			completedBy: userRef,
			createdDate: Timestamp.now(),
		};
		console.log('BODY for activities', body);
		await addDoc(collection(firestore, 'activities'), body).then((res) => {
			console.log('Successfully created activity doc for deal update', dealId, res?.id);
		});
	} catch (err) {
		console.log('Error creating activity doc', err);
	}
};

export const createdClosedActivity = async ({ dispatch, getState, properties, dealId }) => {
	try {
		const userData = await getActingUser(dispatch, getState);

		const { userId } = userData ?? {};
		const userRef = doc(firestore, 'user', userId);

		console.log('props: ', properties);

		const body = {
			activityType:
				properties?.dateClosed && properties.completed ? 'ClaimClosed' : 'ClaimReopened',
			dealId,
			completedBy: userRef,
			createdDate: Timestamp.now(),
		};

		console.log('BODY for activities', body);
		await addDoc(collection(firestore, 'activities'), body).then((res) => {
			console.log('Successfully created activity doc for deal update', dealId, res?.id);
		});
	} catch (err) {
		console.log('Error creating activity doc', err);
	}
};

export const createNotification = async ({ dispatch, dealId, action }) => {
	try {
		const res = await dispatch(
			notificationsApi.endpoints.createNotification.initiate({ dealId, action }),
		).unwrap();

		console.log('Successfully created notification', res);
	} catch (err) {
		console.log('Error creating notification', err);
	}
};

export const createDealAssignedNotification = async ({
	dispatch,
	getState,
	dealId,
	assigneeDocId,
}) => {
	if (!dealId || !assigneeDocId) return;

	try {
		const userData = await getActingUser(dispatch, getState);

		if (String(userData?.userId) === String(assigneeDocId)) return;

		await createNotification({ dispatch, dealId, action: 'assigned' });
	} catch (err) {
		console.log('Error creating deal assignment notification', err);
	}
};

// Deals whose client the user owns are visible even outside their broker groups.
export const checkDealAccess = async (dealId, user) => {
	if (!dealId) return null;

	if (isFirebaseId(dealId)) {
		const docRef = doc(firestore, DEALS_COLLECTION, dealId);
		const dealDoc = await getDoc(docRef);
		return dealDoc;
	}

	// A numeric id is a HubSpot id (e.g. from an old link).
	const collectionRef = collection(firestore, DEALS_COLLECTION);
	const constraints = [where('dealHubspotId', '==', dealId)];

	let docs = await queryViewableRecords({
		collectionRef,
		constraints,
		user,
		ownerFields: ['ownerDocId', 'clientOwnerDocId'],
	});

	if (docs.length > 0) {
		return docs[0];
	}

	// A group viewer's query above was by broker group, so look outside it too.
	if (canViewGroup(user) && user?.userId) {
		docs = await queryByOwnerId({
			collectionRef,
			ownerField: 'clientOwnerDocId',
			ownerValue: user.userId,
			constraints,
		});

		if (docs.length > 0) {
			return docs[0];
		}
	}

	return null;
};
